import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate, useLocation, Link } from "react-router-dom";
import { Table, Popover } from "antd";
import MainLayout from "../../Layout/mainLayout";
import HierarchyChain from "./hierarchyChain";
import { ListPartnersByHierarchy } from "../../Redux/Actions/franchiseManagerAction";
import "./viewFranchiseManger.css";

const formatDate = (iso) => {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString("en-IN", {
            day: "2-digit",
            month: "short",
            year: "numeric",
        });
    } catch {
        return "—";
    }
};

const ListPartners = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const { franchise_id, city_head_id, locality_head_id } = useParams();

    const {
        partnersByHierarchy = [],
        partnersByHierarchyLoading,
    } = useSelector((state) => state.franchiseManager || {});

    useEffect(() => {
        if (!franchise_id) return;

        const payload = { franchise_id };
        if (city_head_id) payload.city_head_id = city_head_id;
        if (locality_head_id) payload.locality_head_id = locality_head_id;

        dispatch(ListPartnersByHierarchy(payload));
    }, [franchise_id, city_head_id, locality_head_id, dispatch]);

    // Derive hierarchy names directly from the populated records
    const franchiseManagerName = partnersByHierarchy[0]?.franchise_id?.name;
    const cityHeadName = Array.isArray(partnersByHierarchy[0]?.city_head_id)
        ? partnersByHierarchy[0]?.city_head_id?.find((ch) => ch._id === city_head_id)?.name
        : undefined;
    const localityHeadName = Array.isArray(partnersByHierarchy[0]?.locality_head)
        ? partnersByHierarchy[0]?.locality_head?.find(
            (lh) => lh.locality_head?._id === locality_head_id
        )?.locality_head?.name
        : undefined;

    const hierarchyNodes = [{ label: "Franchise Manager", name: franchiseManagerName }];
    if (city_head_id) hierarchyNodes.push({ label: "City Head", name: cityHeadName });
    if (locality_head_id) hierarchyNodes.push({ label: "Locality Head", name: localityHeadName });
    hierarchyNodes.push({ label: "Partners", name: "Viewing all", active: true });

    const backPath = locality_head_id
        ? `/franchise/${franchise_id}/city-heads/${city_head_id}/locality-heads`
        : city_head_id
            ? `/franchise/${franchise_id}/city-heads`
            : location.state?.from || "/franchise";

    const columns = [
        {
            title: "Sr No.",
            align: "center",
            render: (_, __, index) => index + 1,
        },
        {
            title: "Name",
            align: "center",
            dataIndex: "name",
            key: "name",
            render: (name) => (
                <span
                    title={name}
                    style={{
                        display: "inline-block",
                        maxWidth: 120,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        verticalAlign: "middle",
                    }}
                >
                    {name}
                </span>
            ),
        },
        {
            title: "Email",
            align: "center",
            dataIndex: "email",
            key: "email",
            render: (email) => (
                <span
                    title={email}
                    style={{
                        display: "inline-block",
                        maxWidth: 160,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        verticalAlign: "middle",
                    }}
                >
                    {email}
                </span>
            ),
        },
        {
            title: "Phone",
            align: "center",
            key: "contact_number",
            render: (_, record) =>
                record.contact_number ? `${record.country_code || ""} ${record.contact_number}` : "—",
        },
        {
            title: "Agent ID",
            align: "center",
            dataIndex: "staff_id",
            key: "staff_id",
        },
        {
            title: "Franchise",
            align: "center",
            key: "franchise",
            render: (_, record) => record.franchise_id?.name || "—",
        },
        {
            title: "City Head",
            align: "center",
            key: "city_head",
            render: (_, record) => {
                const cityHeads = record.city_head_id || [];
                return (
                    <Popover
                        title="City Heads"
                        trigger="click"
                        content={
                            <div
                                style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 4,
                                    maxWidth: 200,
                                    maxHeight: 240,
                                    overflowY: "auto",
                                }}
                            >
                                {cityHeads.length > 0
                                    ? cityHeads.map((ch) => <span key={ch._id}>{ch.name}</span>)
                                    : <span>None</span>}
                            </div>
                        }
                    >
                        <span
                            style={{
                                cursor: "pointer",
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                minWidth: 26,
                                height: 26,
                                padding: "0 8px",
                                borderRadius: 999,
                                background: "#eff6ff",
                                color: "#2563eb",
                                fontSize: 12.5,
                                fontWeight: 600,
                            }}
                        >
                            {cityHeads.length}
                        </span>
                    </Popover>
                );
            },
        },
        {
            title: "Localities",
            align: "center",
            key: "locality",
            render: (_, record) => {
                const localityHeads = record.locality_head || [];
                return (
                    <Popover
                        title="Localities"
                        trigger="click"
                        content={
                            <div
                                style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 4,
                                    maxWidth: 220,
                                    maxHeight: 240,
                                    overflowY: "auto",
                                }}
                            >
                                {localityHeads.length > 0
                                    ? localityHeads.map((lh) => (
                                        <span key={lh._id}>{lh.locality?.name}</span>
                                    ))
                                    : <span>None</span>}
                            </div>
                        }
                    >
                        <span
                            style={{
                                cursor: "pointer",
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                minWidth: 26,
                                height: 26,
                                padding: "0 8px",
                                borderRadius: 999,
                                background: "#f5f3ff",
                                color: "#7c3aed",
                                fontSize: 12.5,
                                fontWeight: 600,
                            }}
                        >
                            {localityHeads.length}
                        </span>
                    </Popover>
                );
            },
        },
        {
            title: "Status",
            align: "center",
            dataIndex: "status",
            key: "status",
            render: (status) => (
                <span
                    className={`vfm-status-badge vfm-status-badge--${status === "active" || status === "unblock" ? "active" : "inactive"
                        }`}
                >
                    <span className="vfm-status-dot" />
                    {status || "—"}
                </span>
            ),
        },
        {
            title: "Joined On",
            align: "center",
            dataIndex: "created_at",
            key: "created_at",
            render: (date) => formatDate(date),
        },
        {
            title: "Details",
            align: "center",
            key: "view",
            render: (_, record) => (
                <Link
                    to={`/franchise/partner/view/${record._id}`}
                    className="vfm-btn vfm-btn--primary"
                    style={{ padding: "6px 14px", fontSize: 12.5, color: "white" }}
                >
                    View
                </Link>
            ),
        },
    ];

    return (
        <MainLayout>
            <div className="vfm-wrapper">
                <div className="vfm-header">
                    <div className="vfm-header-left">
                        <button
                            className="vfm-back-btn"
                            onClick={() => navigate(backPath)}
                            type="button"
                            aria-label="Go back"
                        >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polyline points="15 18 9 12 15 6" />
                            </svg>
                        </button>
                        <div>
                            <h1 className="vfm-title">Partners</h1>
                            <p className="vfm-subtitle">
                                {locality_head_id
                                    ? "Partners assigned under this locality head"
                                    : city_head_id
                                        ? "Partners assigned under this city head"
                                        : "All partners under this franchise manager"}
                            </p>
                        </div>
                    </div>
                </div>

                <HierarchyChain nodes={hierarchyNodes} />

                <div className="vfm-section-card">
                    <Table
                        dataSource={Array.isArray(partnersByHierarchy) ? partnersByHierarchy : []}
                        columns={columns}
                        loading={partnersByHierarchyLoading}
                        rowKey="_id"
                        pagination={{ pageSize: 10 }}
                        scroll={{ x: 1100 }}
                    />
                </div>
            </div>
        </MainLayout>
    );
};

export default ListPartners;